/**
 * O Fechamento com os três cartões de folha e o "Importar Folha XLSX".
 *
 * Papel: monta a `AbaFechamento` e enche os encaixes dela — "Folha de
 * Pagamento" com as folhas fechadas do mês, "Lançamentos de Outros Pagamentos"
 * com os lançamentos de outros bancos, "Pagamentos por Outros Bancos" com a
 * folha XLSX — e o "Importar Folha XLSX" da linha de ações (l. 709–711,
 * 3341–3424, 3497–3516).
 *
 * Como usar: é o painel que a página põe na aba Fechamento. Os cartões leem a
 * mesma consulta de Outros Bancos que a aba Outros Bancos lê, e uma gravação
 * numa aba aparece na outra sem nada além da invalidação do cache. Quem só tem
 * `ver` não vê "Importar Folha XLSX" nem os dois botões de remover.
 *
 * A planilha é lida no navegador e nunca sai dele: só os itens somados vão à
 * API, que substitui a folha do mês. A falha de leitura avisa "Erro ao ler o
 * arquivo: " seguido da mensagem do SheetJS, que fala do arquivo e nunca do
 * conteúdo dele.
 *
 * A planilha só é lida com a configuração de `useConfiguracao` já chegada,
 * porque a aba é escolhida pela sigla do pagador: com a sigla vazia, toda aba
 * do ano casaria, e a soma de outro pagador seria gravada. Antes, a escolha
 * avisa "Aguarde a configuração carregar" sem ler nada e, se a consulta falhou,
 * pede a configuração de novo.
 */
import { acessoAlcanca } from '@concierge/shared';
import { IconFileImport } from '@tabler/icons-react';
import { useRef } from 'react';

import { useNotificacao } from '@/shared/embutido';
import { useAcesso } from '@/shared/hooks';
import { lerPlanilha } from '@/shared/lib/planilha';
import { folhaDoMes, resumoDosOutrosPagamentos } from '../cartoes-do-fechamento';
import { useCasaDoCaixa } from '../hooks/usar-financas';
import {
  useConfiguracao,
  useFolhaImportada,
  useGravarFolhaImportada,
  useOutrosBancos,
  useRemoverFolhaImportada,
  useRemoverFolhasFechadas,
} from '../hooks/usar-financas-extras';
import { lerFolhaXlsx } from '../importacao/folha-xlsx';
import { AbaFechamento } from './aba-fechamento';
import {
  CartaoFolhaDePagamento,
  CartaoOutrosPagamentos,
  CartaoPagamentosPorOutrosBancos,
} from './cartoes-de-folha';
import { Botao } from './kit/botao';
import { EntradaDeArquivo } from './kit/entrada-de-arquivo';

interface PainelDeFechamentoProps {
  /** O mês aberto, em `AAAA-MM`. */
  competencia: string;
  /** Anda o mês aberto: -1 pela seta ‹, +1 pela ›. */
  onMudarMes: (meses: number) => void;
}

/**
 * O texto de uma falha, para o aviso vermelho.
 *
 * @param erro - O que a leitura ou a gravação lançou.
 * @returns A mensagem do erro, que fala do arquivo ou vem da API.
 */
function mensagemDe(erro: unknown): string {
  return erro instanceof Error ? erro.message : 'Não foi possível gravar.';
}

/**
 * O botão "Importar Folha XLSX" com o seletor de arquivo escondido.
 *
 * @param onArquivo - Chamado com a planilha escolhida.
 * @returns O botão e o seletor.
 */
function ImportarFolhaXlsx({ onArquivo }: { onArquivo: (arquivo: File) => void }) {
  const entrada = useRef<HTMLInputElement>(null);
  return (
    <>
      <Botao variante="fantasma" tamanho="sm" onClick={() => entrada.current?.click()}>
        <IconFileImport size={13} aria-hidden="true" /> Importar Folha XLSX
      </Botao>
      <EntradaDeArquivo
        ref={entrada}
        aceita=".xlsx,.xlsm"
        rotulo="Planilha da folha"
        onArquivo={onArquivo}
      />
    </>
  );
}

/**
 * O painel da aba Fechamento.
 *
 * @param competencia - O mês aberto.
 * @param onMudarMes - Anda o mês aberto.
 * @returns A aba Fechamento com os cartões de folha.
 */
export function PainelDeFechamento({ competencia, onMudarMes }: PainelDeFechamentoProps) {
  const casa = useCasaDoCaixa();
  const acesso = useAcesso();
  const podeEditar =
    acesso.data !== undefined && acessoAlcanca(acesso.data, 'financas', 'editar');
  const notificar = useNotificacao();
  const consulta =
    casa.residenciaId === null ? null : { residenciaId: casa.residenciaId, competencia };
  const outrosBancos = useOutrosBancos(consulta);
  const folhaImportada = useFolhaImportada(consulta);
  const configuracao = useConfiguracao(casa.residenciaId);
  const removerFechadas = useRemoverFolhasFechadas();
  const removerFolha = useRemoverFolhaImportada();
  const gravarFolha = useGravarFolhaImportada();

  const folha = folhaDoMes(outrosBancos.data?.folhasFechadas ?? []);
  const resumo = resumoDosOutrosPagamentos(outrosBancos.data?.lancamentos ?? []);
  const importada = folhaImportada.data;

  /** "✕ Remover fechamento": as folhas fechadas de todos do mês (l. 3509–3515). */
  async function handleRemoverFechamento() {
    if (consulta === null || !window.confirm('Remover o fechamento da folha deste mês?'))
      return;
    try {
      await removerFechadas.mutateAsync(consulta);
      notificar('Fechamento removido');
    } catch (erro) {
      notificar(mensagemDe(erro), false);
    }
  }

  /** "✕ Remover folha": a folha XLSX do mês (l. 3453–3459). */
  async function handleRemoverFolha() {
    if (consulta === null || !window.confirm('Remover dados da folha deste mês?')) return;
    try {
      await removerFolha.mutateAsync(consulta);
      notificar('Folha removida');
    } catch (erro) {
      notificar(mensagemDe(erro), false);
    }
  }

  /**
   * Lê a planilha escolhida e grava a folha do mês aberto.
   *
   * @param arquivo - A planilha.
   */
  async function handleImportarFolha(arquivo: File) {
    if (consulta === null) return;
    const daCasa = configuracao.data;
    if (daCasa === undefined) {
      if (configuracao.isError) void configuracao.refetch();
      notificar('Aguarde a configuração carregar', false);
      return;
    }
    let lida: ReturnType<typeof lerFolhaXlsx>;
    try {
      lida = lerFolhaXlsx(await lerPlanilha(arquivo), daCasa.siglaDoPagador, competencia);
    } catch (erro) {
      notificar(`Erro ao ler o arquivo: ${mensagemDe(erro)}`, false);
      return;
    }
    if ('erro' in lida) {
      notificar(lida.erro, false);
      return;
    }
    try {
      await gravarFolha.mutateAsync({ ...consulta, itens: lida.itens });
      notificar('✓ Folha importada');
    } catch (erro) {
      notificar(mensagemDe(erro), false);
    }
  }

  return (
    <AbaFechamento
      competencia={competencia}
      onMudarMes={onMudarMes}
      folhaDePagamento={
        folha !== null && (
          <CartaoFolhaDePagamento
            folha={folha}
            onRemover={podeEditar ? () => void handleRemoverFechamento() : undefined}
          />
        )
      }
      outrosPagamentos={resumo !== null && <CartaoOutrosPagamentos resumo={resumo} />}
      pagamentosPorOutrosBancos={
        importada != null &&
        importada.itens.length > 0 && (
          <CartaoPagamentosPorOutrosBancos
            folha={importada}
            siglaDoPagador={configuracao.data?.siglaDoPagador ?? ''}
            onRemover={podeEditar ? () => void handleRemoverFolha() : undefined}
          />
        )
      }
      importarFolha={
        podeEditar && (
          <ImportarFolhaXlsx onArquivo={(arquivo) => void handleImportarFolha(arquivo)} />
        )
      }
    />
  );
}
